import {signal, WritableSignal} from '@angular/core';
import {HttpClient, HttpResponse} from '@angular/common/http';
import {map, Observable} from 'rxjs';
import {DATE_FORMAT, ISO_DATE_REGEX, ISO_DATETIME_REGEX, SERVER_API_URL} from '../../shared/utils.constants';
import {addMinutes, format, formatISO, isValid} from 'date-fns';

/**
 * Main service class for any entity
 *
 * T represent the Entity type
 * U represent the type of the id field (for deletion)
 */
export class BaseEntityService<T, U> {
    public resourceUrl = SERVER_API_URL;
    // List that will contains the entities
    types: WritableSignal<T[]> = signal<T[]>([]);

    constructor(protected endpointUrl: string, protected http: HttpClient) {
        this.resourceUrl = SERVER_API_URL + endpointUrl;
    }

    create(data: T): Observable<HttpResponse<T>> {
        this.formatFromClient(data);
        return this.http.post<T>(this.resourceUrl, data, {observe: 'response'}).pipe(
            map((res: HttpResponse<T>) => {
                this.formatFromServer(res.body);
                return res;
            })
        );
    }

    update(data: T): Observable<HttpResponse<T>> {
        this.formatFromClient(data);
        return this.http.put<T>(this.resourceUrl, data, {observe: 'response'}).pipe(
            map((res: HttpResponse<T>) => {
                this.formatFromServer(res.body);
                return res;
            })
        );
    }

    find(id: U): Observable<HttpResponse<T>> {
        return this.http.get<T>(`${this.resourceUrl}/${id}`, {observe: 'response'}).pipe(
            map((res: HttpResponse<T>) => {
                this.formatFromServer(res.body);
                return res;
            })
        );
    }

    findAll(): Observable<HttpResponse<T[]>> {
        return this.http.get<T>(`${this.resourceUrl}/all`, {observe: 'response'}).pipe(
            map((res: HttpResponse<any>) => {
                this.formatFromServer(res.body);
                return res;
            })
        );
    }

    delete(id: U): Observable<HttpResponse<any>> {
        return this.http.delete(`${this.resourceUrl}/${id}`, {
            observe: 'response',
        });
    }

    /**
     * Format the given entity (recursively) to be sent to the back for saving in database
     * - It formats any date to the correct Moment format
     * - Any field that has 'null' as value will be set to 'undefined' in order to be ignore by Jackson Json parsing
     * @param entity
     */
    formatFromClient(entity: any) {
        this.onFormatFromClient(entity);
        this.recursiveFromClient(entity);
    }

    recursiveFromClient(entity: any) {
        // Empty entity, return
        if (entity === null || entity === undefined || entity === 'null') {
            entity = undefined;
            return;
        }
        // Case string value or case number, return
        if (typeof entity === 'string' || entity instanceof String || !isNaN(entity)) {
            return;
        }
        Object.entries(entity).map((entry) => {
            if (entity[entry[0]] === null || entity[entry[0]] === undefined || entity[entry[0]] === 'null') {
                entity[entry[0]] = undefined;
            } else {
                // Case an array, recursive on each element
                if (Array.isArray(entity[entry[0]]) && !(entity[entry[0]] instanceof String)) {
                    entity[entry[0]].forEach((ent: any) => {
                        this.recursiveFromClient(ent);
                    });
                }
                // Case an object but not a date, recursive on the object fields
                else if (
                    entity[entry[0]] instanceof Object &&
                    !(entity[entry[0]] instanceof String) && !('_isAMomentObject' in entity[entry[0]]) &&
                    !(entity[entry[0]] instanceof Date)
                ) {
                    this.recursiveFromClient(entity[entry[0]]);
                }
                // Case a date, make sure to format it properly
                else if (
                    entity[entry[0]] instanceof Object &&
                    ('_isAMomentObject' in entity[entry[0]] || entity[entry[0]] instanceof Date)
                ) {
                    if (entity[entry[0]] instanceof Date) {
                        const date = entity[entry[0]];
                        entity[entry[0]] = addMinutes(date, date.getTimezoneOffset());
                    } else if (isValid(formatISO(new Date(entity[entry[0]])))) {
                        const date = new Date(entity[entry[0]]);
                        entity[entry[0]] = addMinutes(date, date.getTimezoneOffset());
                    } else if (isValid(format(new Date(entity[entry[0]]), DATE_FORMAT))) {
                        const date = new Date(entity[entry[0]]);
                        entity[entry[0]] = format(addMinutes(date, date.getTimezoneOffset()), DATE_FORMAT);
                    }
                }
                // Case string, we trim both side
                else if ((typeof entity[entry[0]] === 'string' || entity[entry[0]] instanceof String) && isNaN(entity[entry[0]])) {
                    entity[entry[0]] = entity[entry[0]].trim();
                }
            }
        });
    }

    /**
     * Function that can be overriden in child services to add default values when saving to database for example
     * @param entity
     */
    onFormatFromClient(entity: any) {
        //
    }

    /**
     * Format the given entity (recursively) for proper use on the web project
     * - It transform dates (string) as Moment object
     * @param entity
     */
    formatFromServer(entity: any) {
        this.onFormatFromServer(entity);
        this.recursiveFromServer(entity);
    }

    recursiveFromServer(entity: any) {
        // Empty entity, return
        if (entity === null || entity === undefined || entity === 'null') {
            entity = undefined;
            return;
        }
        if (entity instanceof Array) {
            entity.forEach((el) => {
                this.formatFromServer(el);
            });
        } else {
            Object.entries(entity).map((entry) => {
                if (entity[entry[0]] === null || entity[entry[0]] === undefined || entity[entry[0]] === 'null') {
                    entity[entry[0]] = undefined;
                } else {
                    // Case an array recursive on each element
                    if (Array.isArray(entity[entry[0]]) && !(entity[entry[0]] instanceof String)) {
                        entity[entry[0]].forEach((ent: any) => {
                            this.recursiveFromServer(ent);
                        });
                    }
                    // Case an object, but not a date, recursive on the object fields
                    else if (entity[entry[0]] instanceof Object && !(entity[entry[0]] instanceof String)) {
                        this.recursiveFromServer(entity[entry[0]]);
                    }
                    // Case a date, but not a number, make sure to create a Moment object
                    else if (
                        entry[0] &&
                        entry[0].toLowerCase()?.indexOf('date') >= 0 &&
                        isNaN(entity[entry[0]]) &&
                        ISO_DATE_REGEX.test(entity[entry[0]]) &&
                        isValid(formatISO(new Date(entity[entry[0]])))
                    ) {
                        entity[entry[0]] = new Date(entity[entry[0]]);
                    }
                    // Case a datetime, but not a number, make sure to create a Moment object
                    else if (
                        entry[0] &&
                        entry[0].toLowerCase()?.indexOf('date') >= 0 &&
                        isNaN(entity[entry[0]]) &&
                        ISO_DATETIME_REGEX.test(entity[entry[0]]) &&
                        isValid(formatISO(new Date(entity[entry[0]])))
                    ) {
                        entity[entry[0]] = new Date(entity[entry[0]]);
                    }
                }
            });
        }
    }


    /**
     * Function that can be overriden in child services when retrieving from database
     * @param entity
     */
    onFormatFromServer(entity: any) {
        //
    }
}
